import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useParams, useNavigate } from 'react-router-dom';
import { rootUrl } from '../../config/config';
import { useAuth } from '../../components/myTrack/AuthContext';

const EditTransaction = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, token } = useAuth();

    const [transaction, setTransaction] = useState(null);
    const [date, setDate] = useState('');
    const [week, setWeek] = useState('');
    const [validWeeks, setValidWeeks] = useState([]);
    const [weeksMeta, setWeeksMeta] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [warning, setWarning] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const headers = { Authorization: `Bearer ${token || localStorage.getItem('authToken')}` };

                const [txRes, validWeeksRes, weeksRes] = await Promise.all([
                    axios.get(`${rootUrl}/all_transactions/${id}`, { headers }),
                    axios.get(`${rootUrl}/system/valid_weeks?past=24`, { headers }),
                    axios.get(`${rootUrl}/system/weeks`, { headers })
                ]);

                const tx = txRes.data;
                setTransaction(tx);
                setDate(tx.date ? tx.date.split('T')[0] : '');
                setWeek(tx.week || '');

                setValidWeeks((validWeeksRes.data && validWeeksRes.data.valid_weeks) || []);
                setWeeksMeta((weeksRes.data && weeksRes.data.data) || []);
            } catch (err) {
                console.error('Error fetching transaction or weeks', err);
                setError('Failed to load transaction or weeks');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, token]);

    const computeWeekFromDate = (d) => {
        if (!d) return null;
        const dt = new Date(d);
        const found = weeksMeta.find(w => {
            const start = new Date(w.start_date);
            const end = new Date(w.end_date);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return dt >= start && dt <= end;
        });
        return found ? found.week : null;
    };

    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setDate(newDate);
        const computed = computeWeekFromDate(newDate);
        if (computed && computed !== week) {
            setWarning(`Computed week for chosen date is ${computed}. You can override but ensure it's intended.`);
            setWeek(computed);
        } else {
            setWarning('');
        }
    };

    const validate = () => {
        setError('');
        const today = new Date();
        if (!date) return 'Date is required';
        if (new Date(date) > today) return 'Date cannot be in the future';
        if (!week) return 'Week is required';
        // check week within validWeeks
        if (validWeeks.length && !validWeeks.includes(week)) return 'Selected week is not valid or is in the future';
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const v = validate();
        if (v) { setError(v); return; }
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${token || localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' };
            const payload = { date, week, edit_by: (user && user.username) || (user && user.name) || 'web' };
            await axios.put(`${rootUrl}/all_transactions/${id}`, payload, { headers });
            navigate('/all_transactions');
        } catch (err) {
            console.error('Error updating transaction', err);
            setError(err.response?.data?.message || 'Failed to update transaction');
        } finally {
            setLoading(false);
        }
    };

    if (loading && !transaction) return <div className="p-6">Loading...</div>;
    if (error && !transaction) return <div className="p-6 text-red-600">{error}</div>;

    return (
        <div className="min-h-screen p-6">
            <div className="max-w-3xl mx-auto bg-white rounded shadow p-6">
                <h1 className="text-2xl font-semibold mb-4">Edit Transaction</h1>
                {error && <div className="text-red-600 mb-3">{error}</div>}
                {warning && <div className="text-yellow-700 mb-3">{warning}</div>}

                {transaction && (
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Plate No</label>
                                <div className="text-gray-700">{transaction.plate_no}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Doc No</label>
                                <div className="text-gray-700">{transaction.doc_no}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Type</label>
                                <div className="text-gray-700">{transaction.type}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Amount (KES)</label>
                                <div className="text-gray-700">{transaction.amount ? parseFloat(transaction.amount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Your Ref</label>
                                <div className="text-gray-700">{transaction.your_ref || 'N/A'}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Our Ref</label>
                                <div className="text-gray-700">{transaction.our_ref || 'N/A'}</div>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Notes</label>
                                <div className="text-gray-700 whitespace-pre-wrap">{transaction.notes || 'N/A'}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Input By</label>
                                <div className="text-gray-700">{transaction.input_by}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Input Date</label>
                                <div className="text-gray-700">{transaction.input_date ? new Date(transaction.input_date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Edit Date</label>
                                <div className="text-gray-700">{transaction.edit_date ? new Date(transaction.edit_date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Driver</label>
                                <div className="text-gray-700">{transaction.driver_id || 'N/A'}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Owner Code</label>
                                <div className="text-gray-700">{transaction.owner_code || 'N/A'}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Date</label>
                                <input type="date" value={date} onChange={handleDateChange} className="border rounded px-3 py-2 w-full" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Week</label>
                                <Select
                                    classNamePrefix="custom-select"
                                    value={validWeeks.map(w => ({ value: w, label: w })).find(o => o.value === week) || null}
                                    onChange={(opt) => setWeek(opt ? opt.value : '')}
                                    options={[{ value: '', label: 'Select week' }, ...validWeeks.map(w => ({ value: w, label: w }))]}
                                    placeholder="Select week"
                                    isClearable
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button type="submit" disabled={loading} className="bg-indigo-600 text-white px-4 py-2 rounded">Save</button>
                            <button type="button" onClick={() => navigate(-1)} className="bg-gray-200 px-4 py-2 rounded">Cancel</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EditTransaction;
